import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LayersClearIcon from "@mui/icons-material/LayersClear";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  MONTH_NAMES,
  applyGroup,
  bogotaParts,
  daysInMonth,
  formatDayMonth,
  formatLastMinute,
  monthKey,
  monthView,
  planGroup,
  removeGroup,
  removeNatureOwn,
  removeNatureOwnMany,
  resolvePaymentCutoff,
  setMonthDefault,
  setNatureOwn,
  validTime,
} from "../../../paymentCutoff";

// Pestaña "Fechas de corte" de la configuración de contratos
// (payment-monthly-cutoff D6, payment-cutoff-groups-and-time D4). Edita
// config/contracts.paymentCutoff: el interruptor, los días de aviso y el
// calendario de un año. Cada mes tiene tres niveles, y para cada naturaleza
// gana el más específico: configuración propia > grupo > configuración del
// mes. Las naturalezas salen del catálogo natureGroups (default + DEX) y se
// guardan por ETIQUETA, que es lo que guarda contract.nature. Las reglas de
// edición (un grupo por naturaleza, fusión por día + hora, réplica a otros
// meses) viven en el módulo puro; aquí solo se invocan. Cada cambio se
// persiste de inmediato, como el resto de la pantalla.

const sorted = (list) => (list || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// Campos de día como texto numérico y no type="number": con type="number" la
// rueda del ratón cambia el valor del campo enfocado, y como cada cambio se
// guarda al salir del campo, un corte podía moverse sin que nadie lo notara.
// Solo se aceptan dígitos (máximo 2).
const DAY_INPUT_PROPS = { inputMode: "numeric", pattern: "[0-9]*", maxLength: 2 };
const digitsOnly = (value) => String(value || "").replace(/\D/g, "").slice(0, 2);

const validDayInput = (value) => {
  const n = Number(value);
  return String(value).trim() !== "" && Number.isInteger(n) && n >= 1 && n <= 31 ? n : null;
};

// Hora límite tal como se lee en la pantalla: el último minuto permitido.
const untilLabel = (time) => (time ? `hasta las ${formatLastMinute(time)}` : "fin del día (11:59 p. m.)");

// "26 · hasta las 5:59 p. m."
const settingLabel = (day, time) => `${day} · ${untilLabel(time)}`;

// Cierra una frase con punto, salvo que ya termine en uno ("p. m.").
const endSentence = (text) => (text.endsWith(".") ? text : `${text}.`);

// Error de un campo de hora: vacío es fin del día; "00:00" no se acepta.
const timeError = (value) => (value && !validTime(value) ? "Entre 00:01 y 23:59" : "");

// Hora límite: un campo de hora nativo; vacío es "fin del día". El texto de
// ayuda muestra siempre el último minuto permitido, que es como lo lee
// Financiera ("con 18:00 se puede hasta las 5:59 p. m.").
function TimeField({ value, onChange, onCommit, disabled, label = "Hora límite", sx }) {
  const error = timeError(value);
  return (
    <TextField
      type="time"
      size="small"
      label={label}
      value={value || ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onCommit}
      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ step: 60 }}
      error={!!error}
      helperText={error || (value ? `Se puede ${untilLabel(value)}` : "Fin del día (11:59 p. m.)")}
      InputProps={value ? {
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title="Usar fin del día">
              <IconButton
                size="small"
                edge="end"
                disabled={disabled}
                onClick={() => { onChange(""); if (onCommit) onCommit(""); }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </InputAdornment>
        ),
      } : undefined}
      sx={{ width: 190, ...sx }}
    />
  );
}

export default function PaymentCutoffConfig({ value, natureGroups, onSave, saving }) {
  const cfg = useMemo(() => resolvePaymentCutoff(value), [value]);
  const today = useMemo(() => bogotaParts(new Date()), []);
  const [year, setYear] = useState(today.year);
  const [expanded, setExpanded] = useState(today.month);
  // Borradores de la configuración del mes ({ [YYYY-MM]: { day, time } }): se
  // confirman al salir del campo o con Enter, para no guardar en cada tecla.
  const [drafts, setDrafts] = useState({});
  const [draftNotice, setDraftNotice] = useState(String(cfg.noticeDays));
  // Diálogo de configuración propia: { month, nature, day, time, origNature }.
  const [ownDialog, setOwnDialog] = useState(null);
  // Diálogo de grupo: { month, editing: { day, time } | null, natures: [],
  // day, time, replicate: [meses], removeOwn: { [mes]: [naturalezas] } }.
  const [groupDialog, setGroupDialog] = useState(null);
  const [dialogError, setDialogError] = useState("");
  // Confirmación de una acción destructiva: { title, message, confirm, next }.
  // Diálogo de la app y no window.confirm, que bloquea la página.
  const [confirmDialog, setConfirmDialog] = useState(null);

  useEffect(() => {
    setDraftNotice(String(cfg.noticeDays));
  }, [cfg.noticeDays]);

  const groups = useMemo(() => ({
    default: sorted(natureGroups?.default).filter((n) => n && n.enabled !== false),
    dex: sorted(natureGroups?.dex).filter((n) => n && n.enabled !== false),
  }), [natureGroups]);

  // Toda etiqueta del catálogo, activa o no: una configuración sobre una
  // naturaleza desactivada sigue siendo válida (los contratos existentes la
  // conservan).
  const knownLabels = useMemo(() => new Set(
    [...(natureGroups?.default || []), ...(natureGroups?.dex || [])]
      .filter((n) => n && n.label)
      .map((n) => n.label)
  ), [natureGroups]);

  const viewOf = (month) => monthView(cfg, year, month);
  const monthLabel = (month) => capitalize(MONTH_NAMES[month - 1]);

  const unknownTooltip = "Esta naturaleza ya no está en el catálogo: solo aplica a contratos que conserven ese texto";

  // --- Configuración del mes --------------------------------------------------

  const draftOf = (month) => {
    const key = monthKey(year, month);
    const view = viewOf(month);
    const d = drafts[key] || {};
    return {
      day: "day" in d ? d.day : (view.day ?? ""),
      time: "time" in d ? d.time : (view.time || ""),
    };
  };

  const setDraft = (month, patch) => {
    const key = monthKey(year, month);
    setDrafts((all) => ({ ...all, [key]: { ...(all[key] || {}), ...patch } }));
  };

  // `override` permite confirmar un valor recién puesto (botón "fin del día")
  // sin esperar al siguiente render.
  const commitMonth = (month, override) => {
    const key = monthKey(year, month);
    const draft = { ...draftOf(month), ...(override || {}) };
    setDrafts((all) => { const n = { ...all }; delete n[key]; return n; });
    const view = viewOf(month);
    const rawDay = String(draft.day).trim();
    if (rawDay !== "" && validDayInput(rawDay) === null) return;
    if (timeError(draft.time)) return;
    const day = rawDay === "" ? null : Number(rawDay);
    const time = day === null ? null : (draft.time || null);
    if (day === view.day && time === view.time) return;
    onSave(setMonthDefault(cfg, year, month, { day, time }));
  };

  const commitNotice = () => {
    const n = Number(draftNotice);
    if (!Number.isInteger(n) || n < 0 || n > 31) {
      setDraftNotice(String(cfg.noticeDays));
      return;
    }
    if (n !== cfg.noticeDays) onSave({ ...cfg, noticeDays: n });
  };

  // --- Configuración propia ---------------------------------------------------

  const openOwnAdd = (month, nature = "") => {
    setDialogError("");
    setOwnDialog({ month, nature, day: String(viewOf(month).day ?? ""), time: "", origNature: null });
  };

  const openOwnEdit = (month, own) => {
    setDialogError("");
    setOwnDialog({ month, nature: own.nature, day: String(own.day), time: own.time || "", origNature: own.nature });
  };

  const handleOwnSave = async () => {
    if (!ownDialog.nature) {
      setDialogError("Selecciona la naturaleza.");
      return;
    }
    const day = validDayInput(ownDialog.day);
    if (day === null) {
      setDialogError("El día debe ser un número entre 1 y 31.");
      return;
    }
    try {
      const next = setNatureOwn(
        cfg, year, ownDialog.month, ownDialog.nature,
        { day, time: ownDialog.time || null },
        { replace: !!ownDialog.origNature }
      );
      const ok = await onSave(next);
      if (ok !== false) setOwnDialog(null);
    } catch (e) {
      setDialogError(e.message);
    }
  };

  // Naturalezas que se ofrecen: las que aún no tienen configuración propia en
  // ese mes (más la que se está editando).
  const ownOptions = (group) => {
    if (!ownDialog) return [];
    const taken = new Set(viewOf(ownDialog.month).own.map((o) => o.nature));
    return group.filter((n) => !taken.has(n.label) || n.label === ownDialog.origNature);
  };

  const ownSummary = (() => {
    if (!ownDialog || !ownDialog.nature) return null;
    const day = validDayInput(ownDialog.day);
    if (day === null || timeError(ownDialog.time)) return null;
    const effective = Math.min(day, daysInMonth(year, ownDialog.month));
    return `${formatDayMonth(effective, ownDialog.month)} de ${year}, ${untilLabel(ownDialog.time || null)}`;
  })();

  // --- Grupos -----------------------------------------------------------------

  const openGroupCreate = (month) => {
    setDialogError("");
    setGroupDialog({
      month, editing: null, natures: [], day: String(viewOf(month).day ?? ""), time: "", replicate: [], removeOwn: {},
    });
  };

  const openGroupEdit = (month, group) => {
    setDialogError("");
    setGroupDialog({
      month,
      editing: { day: group.day, time: group.time },
      natures: group.natures.slice(),
      day: String(group.day),
      time: group.time || "",
      replicate: [],
      removeOwn: {},
    });
  };

  const groupMonths = groupDialog ? [groupDialog.month, ...groupDialog.replicate] : [];

  const groupInputOf = (d) => ({ day: validDayInput(d.day), time: d.time || null, natures: d.natures });

  // Vista previa por mes, o el motivo por el que todavía no se puede guardar.
  const groupPlan = (() => {
    if (!groupDialog) return { plan: null, reason: null };
    if (groupDialog.natures.length === 0) return { plan: null, reason: "Selecciona al menos una naturaleza." };
    if (validDayInput(groupDialog.day) === null) return { plan: null, reason: "El día debe ser un número entre 1 y 31." };
    if (timeError(groupDialog.time)) return { plan: null, reason: "La hora debe estar entre 00:01 y 23:59." };
    try {
      return {
        plan: planGroup(cfg, year, groupMonths, groupInputOf(groupDialog), { editing: groupDialog.editing }),
        reason: null,
      };
    } catch (e) {
      return { plan: null, reason: e.message };
    }
  })();

  const toggleNature = (label) => setGroupDialog((d) => ({
    ...d,
    natures: d.natures.includes(label) ? d.natures.filter((n) => n !== label) : [...d.natures, label],
  }));

  const setAllNatures = (list, on) => setGroupDialog((d) => {
    const labels = list.map((n) => n.label);
    const rest = d.natures.filter((n) => !labels.includes(n));
    return { ...d, natures: on ? [...rest, ...labels] : rest };
  });

  const toggleReplicate = (month) => setGroupDialog((d) => ({
    ...d,
    replicate: d.replicate.includes(month) ? d.replicate.filter((m) => m !== month) : [...d.replicate, month],
  }));

  const toggleRemoveOwn = (month, nature) => setGroupDialog((d) => {
    const list = d.removeOwn[month] || [];
    const next = list.includes(nature) ? list.filter((n) => n !== nature) : [...list, nature];
    return { ...d, removeOwn: { ...d.removeOwn, [month]: next } };
  });

  const handleGroupSave = async () => {
    if (!groupPlan.plan) return;
    try {
      const next = applyGroup(cfg, year, groupMonths, groupInputOf(groupDialog), {
        editing: groupDialog.editing,
        removeOwnFor: groupDialog.removeOwn,
      });
      const ok = await onSave(next);
      if (ok !== false) setGroupDialog(null);
    } catch (e) {
      setDialogError(e.message);
    }
  };

  const handleGroupRemove = (month, group) => {
    setConfirmDialog({
      title: "Eliminar grupo",
      message: `¿Eliminar el grupo del ${settingLabel(group.day, group.time)} en ${monthLabel(month).toLowerCase()}? `
        + "Sus naturalezas pasan a la configuración del mes (o a su configuración propia).",
      confirm: "Eliminar",
      next: removeGroup(cfg, year, month, group),
    });
  };

  const handleClearOwnOfGroup = (month, group, owned) => {
    setConfirmDialog({
      title: "Quitar configuraciones propias",
      message: `¿Quitar la configuración propia de ${owned.join(", ")}? Pasan a regirse por el grupo del ${endSentence(settingLabel(group.day, group.time))}`,
      confirm: "Quitar",
      next: removeNatureOwnMany(cfg, year, month, owned),
    });
  };

  const handleConfirm = async () => {
    const ok = await onSave(confirmDialog.next);
    if (ok !== false) setConfirmDialog(null);
  };

  // --- Resumen ----------------------------------------------------------------

  const monthSummary = (view) => {
    const parts = [];
    parts.push(view.day ? `Mes: ${settingLabel(view.day, view.time)}` : "Sin configuración del mes");
    if (view.groups.length > 0) parts.push(`${view.groups.length} ${view.groups.length === 1 ? "grupo" : "grupos"}`);
    if (view.own.length > 0) parts.push(`${view.own.length} ${view.own.length === 1 ? "propia" : "propias"}`);
    return parts.join(" · ");
  };

  const isCurrentYear = year === today.year;
  const currentView = isCurrentYear ? viewOf(today.month) : null;
  const currentEmpty = currentView && !currentView.day && currentView.groups.length === 0 && currentView.own.length === 0;

  const natureChip = (label, extra) => {
    const unknown = !knownLabels.has(label);
    const chip = (
      <Chip
        label={label}
        size="small"
        color={unknown ? "warning" : "default"}
        icon={unknown ? <WarningAmberIcon /> : undefined}
        {...extra}
      />
    );
    return unknown ? <Tooltip key={label} title={unknownTooltip}>{chip}</Tooltip> : <React.Fragment key={label}>{chip}</React.Fragment>;
  };

  const renderMonth = (month) => {
    const key = monthKey(year, month);
    const view = viewOf(month);
    const draft = draftOf(month);
    const isCurrent = isCurrentYear && month === today.month;
    const ownByNature = new Map(view.own.map((o) => [o.nature, o]));
    return (
      <Accordion
        key={key}
        expanded={expanded === month}
        onChange={(_, open) => setExpanded(open ? month : null)}
        disableGutters
        variant="outlined"
        sx={isCurrent ? { bgcolor: "action.hover" } : undefined}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }} alignItems={{ sm: "center" }} sx={{ width: "100%" }}>
            <Typography sx={{ width: 190, fontWeight: isCurrent ? 600 : 400 }}>
              {monthLabel(month)}
              {isCurrent && <Chip label="Mes en curso" size="small" sx={{ ml: 1 }} />}
            </Typography>
            <Typography variant="body2" color="text.secondary">{monthSummary(view)}</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle2" gutterBottom>Configuración del mes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Aplica a las naturalezas que no están en un grupo ni tienen configuración propia.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
            <TextField
              size="small"
              label="Día"
              placeholder="Sin corte"
              value={draft.day}
              inputProps={{ ...DAY_INPUT_PROPS, "aria-label": `Día del mes de ${MONTH_NAMES[month - 1]}` }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setDraft(month, { day: digitsOnly(e.target.value) })}
              onBlur={() => commitMonth(month)}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              disabled={saving}
              helperText={draft.day === "" ? "Sin corte" : " "}
              sx={{ width: 110 }}
            />
            <TimeField
              value={draft.time}
              disabled={saving || String(draft.day).trim() === ""}
              onChange={(t) => setDraft(month, { time: t })}
              onCommit={(t) => commitMonth(month, typeof t === "string" ? { time: t } : undefined)}
            />
          </Stack>

          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Grupos</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => openGroupCreate(month)} disabled={saving}>
              Crear grupo
            </Button>
          </Stack>
          {view.groups.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sin grupos. Un grupo aplica el mismo día y hora a varias naturalezas.
            </Typography>
          )}
          {view.groups.map((g) => {
            const owned = g.natures.filter((n) => ownByNature.has(n));
            return (
              <Box key={`${g.day}|${g.time || ""}`} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                    {formatDayMonth(Math.min(g.day, daysInMonth(year, month)), month)} · {untilLabel(g.time)}
                  </Typography>
                  <Tooltip title="Editar grupo">
                    <span>
                      <IconButton size="small" onClick={() => openGroupEdit(month, g)} disabled={saving}>
                        <EditIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {owned.length > 0 && (
                    <Tooltip title="Quitar las configuraciones propias de sus naturalezas">
                      <span>
                        <IconButton size="small" onClick={() => handleClearOwnOfGroup(month, g, owned)} disabled={saving}>
                          <LayersClearIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Eliminar grupo">
                    <span>
                      <IconButton size="small" onClick={() => handleGroupRemove(month, g)} disabled={saving}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {g.natures.map((label) => {
                    const own = ownByNature.get(label);
                    if (!own) return natureChip(label);
                    return (
                      <Tooltip
                        key={label}
                        title={`Tiene configuración propia (${settingLabel(own.day, own.time)}), que manda sobre el grupo`}
                      >
                        <Chip label={`${label} · propia`} size="small" variant="outlined" color="info" />
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            );
          })}

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Configuraciones propias</Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={() => openOwnAdd(month)} disabled={saving}>
              Agregar configuración propia
            </Button>
          </Stack>
          {view.own.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              Sin configuraciones propias. Una configuración propia manda sobre el grupo y el mes.
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {view.own.map((o) => natureChip(o.nature, {
              label: `${o.nature}: ${settingLabel(o.day, o.time)}`,
              onClick: () => openOwnEdit(month, o),
              onDelete: saving ? undefined : () => onSave(removeNatureOwn(cfg, year, month, o.nature)),
            }))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Lista de casillas de un grupo del catálogo, con "seleccionar todas".
  const natureCheckList = (title, list) => {
    if (list.length === 0) return null;
    const allOn = list.every((n) => groupDialog.natures.includes(n.label));
    return (
      <Box sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">{title}</Typography>
          <Button size="small" onClick={() => setAllNatures(list, !allOn)}>
            {allOn ? "Quitar todas" : "Seleccionar todas"}
          </Button>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          {list.map((n) => (
            <FormControlLabel
              key={n.key || n.label}
              control={<Checkbox size="small" checked={groupDialog.natures.includes(n.label)} onChange={() => toggleNature(n.label)} />}
              label={<Typography variant="body2">{n.label}</Typography>}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Miembros del grupo editado que ya no están en el catálogo: se muestran
  // para poder quitarlos.
  const outOfCatalog = groupDialog
    ? groupDialog.natures.filter((n) => !knownLabels.has(n)).map((label) => ({ key: label, label }))
    : [];

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems={{ sm: "center" }}>
            <FormControlLabel
              control={(
                <Switch
                  checked={cfg.enabled}
                  disabled={saving}
                  onChange={(e) => onSave({ ...cfg, enabled: e.target.checked })}
                />
              )}
              label={cfg.enabled ? "Fecha de corte activa" : "Fecha de corte inactiva"}
            />
            <TextField
              label="Días de aviso previo"
              size="small"
              value={draftNotice}
              inputProps={DAY_INPUT_PROPS}
              onChange={(e) => setDraftNotice(digitsOnly(e.target.value))}
              onBlur={commitNotice}
              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              sx={{ width: 180 }}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Desde la hora límite del día de corte y hasta el fin del mes, contratistas y supervisores
            no pueden presentar ni aprobar actas o facturas; el día 1 se reabre. La hora límite no se
            incluye: con 18:00 se puede hasta las 5:59 p. m. Sin hora, el corte es al fin del día. Todo
            en hora de Colombia. Para cada naturaleza manda su configuración propia; si no tiene, la
            de su grupo; si no está en un grupo, la del mes. Un mes sin ninguna no tiene corte. Los
            usuarios ADMIN quedan exentos.
          </Typography>
        </CardContent>
      </Card>

      {isCurrentYear && (
        <Alert severity={cfg.enabled ? "info" : "warning"} sx={{ mb: 2 }}>
          <strong>{monthLabel(today.month)} de {today.year}:</strong>{" "}
          {currentEmpty ? "Sin fechas registradas para este mes" : monthSummary(currentView)}
          {currentView.groups.length > 0 && (
            <>
              {" · Grupos: "}
              {currentView.groups.map((g) => `${settingLabel(g.day, g.time)} (${g.natures.length})`).join("; ")}
            </>
          )}
          {currentView.own.length > 0 && (
            <>
              {" · Propias: "}
              {currentView.own.map((o) => `${o.nature} (${settingLabel(o.day, o.time)})`).join(", ")}
            </>
          )}
          {!cfg.enabled && " · El corte está inactivo: no se bloquea nada ni se muestra aviso."}
          {cfg.enabled && currentEmpty && " · Con el corte activo y sin fechas, este mes no bloquea nada."}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">Calendario de cortes</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <IconButton size="small" onClick={() => setYear((y) => y - 1)} aria-label="Año anterior">
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 60, textAlign: "center" }}>{year}</Typography>
              <IconButton size="small" onClick={() => setYear((y) => y + 1)} aria-label="Año siguiente">
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Stack>
          {MONTH_NAMES.map((_, idx) => renderMonth(idx + 1))}
        </CardContent>
      </Card>

      <Dialog open={!!ownDialog} onClose={() => setOwnDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          {ownDialog?.origNature ? "Editar configuración propia" : "Agregar configuración propia"}
          {ownDialog && ` — ${monthLabel(ownDialog.month)} de ${year}`}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="cutoff-nature-label">Naturaleza</InputLabel>
            <Select
              labelId="cutoff-nature-label"
              label="Naturaleza"
              value={ownDialog?.nature || ""}
              disabled={!!ownDialog?.origNature}
              onChange={(e) => setOwnDialog((d) => ({ ...d, nature: e.target.value }))}
            >
              {ownDialog?.origNature && !knownLabels.has(ownDialog.origNature) && (
                <MenuItem value={ownDialog.origNature}>{ownDialog.origNature} (fuera del catálogo)</MenuItem>
              )}
              <ListSubheader>Naturalezas estándar (CPSDF / RCPS)</ListSubheader>
              {ownOptions(groups.default).map((n) => (
                <MenuItem key={`default-${n.key}`} value={n.label}>{n.label}</MenuItem>
              ))}
              <ListSubheader>Convenios Dirección de Extensión (DEX)</ListSubheader>
              {ownOptions(groups.dex).map((n) => (
                <MenuItem key={`dex-${n.key}`} value={n.label}>{n.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mt: 2 }}>
            <TextField
              label="Día de corte"
              size="small"
              value={ownDialog?.day || ""}
              inputProps={DAY_INPUT_PROPS}
              onChange={(e) => setOwnDialog((d) => ({ ...d, day: digitsOnly(e.target.value) }))}
              helperText="Un día mayor que los del mes se toma como el último."
              sx={{ width: 200 }}
            />
            <TimeField
              value={ownDialog?.time || ""}
              onChange={(t) => setOwnDialog((d) => ({ ...d, time: t }))}
            />
          </Stack>
          {/* Resumen de lo que se va a guardar: un clic adelantado en la lista
              podía dejar elegida otra naturaleza sin que se notara. */}
          {ownSummary && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Se guardará: <strong>{ownDialog.nature}</strong> con corte el <strong>{ownSummary}</strong>{ownSummary.endsWith(".") ? "" : "."}
            </Alert>
          )}
          {dialogError && <Alert severity="warning" sx={{ mt: 2 }}>{dialogError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnDialog(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !ownSummary} onClick={handleOwnSave}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!groupDialog} onClose={() => setGroupDialog(null)} fullWidth maxWidth="md">
        <DialogTitle>
          {groupDialog?.editing ? "Editar grupo" : "Crear grupo"}
          {groupDialog && ` — ${monthLabel(groupDialog.month)} de ${year}`}
        </DialogTitle>
        {groupDialog && (
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              El grupo aplica el mismo día y hora a las naturalezas elegidas. Una naturaleza está en un
              solo grupo por mes; su configuración propia, si la tiene, sigue mandando.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" sx={{ mb: 2 }}>
              <TextField
                label="Día de corte"
                size="small"
                value={groupDialog.day}
                inputProps={DAY_INPUT_PROPS}
                onChange={(e) => setGroupDialog((d) => ({ ...d, day: digitsOnly(e.target.value) }))}
                helperText="Un día mayor que los del mes se toma como el último."
                sx={{ width: 200 }}
              />
              <TimeField
                value={groupDialog.time}
                onChange={(t) => setGroupDialog((d) => ({ ...d, time: t }))}
              />
            </Stack>

            <Typography variant="subtitle1" gutterBottom>
              Naturalezas ({groupDialog.natures.length})
            </Typography>
            {natureCheckList("Naturalezas estándar (CPSDF / RCPS)", groups.default)}
            {natureCheckList("Convenios Dirección de Extensión (DEX)", groups.dex)}
            {natureCheckList("Fuera del catálogo", outOfCatalog)}

            {!groupDialog.editing && (
              <>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle1">Replicar en otros meses de {year}</Typography>
                  <Stack direction="row">
                    <Button
                      size="small"
                      onClick={() => setGroupDialog((d) => ({
                        ...d,
                        replicate: MONTH_NAMES.map((__, i) => i + 1).filter((m) => m > d.month),
                      }))}
                    >
                      Resto del año
                    </Button>
                    <Button size="small" onClick={() => setGroupDialog((d) => ({ ...d, replicate: [] }))}>
                      Ninguno
                    </Button>
                  </Stack>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Cada mes recibe una copia independiente: después se edita mes por mes.
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" } }}>
                  {MONTH_NAMES.map((name, i) => i + 1).filter((m) => m !== groupDialog.month).map((m) => (
                    <FormControlLabel
                      key={m}
                      control={<Checkbox size="small" checked={groupDialog.replicate.includes(m)} onChange={() => toggleReplicate(m)} />}
                      label={<Typography variant="body2">{monthLabel(m)}</Typography>}
                    />
                  ))}
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>Vista previa</Typography>
            {groupPlan.reason && <Alert severity="info">{groupPlan.reason}</Alert>}
            {groupPlan.plan && groupPlan.plan.map((p) => {
              const effective = p.clampedDay || p.day;
              const notes = p.moves.length + p.ownOverrides.length + (p.mergeWith ? 1 : 0) + (p.clampedDay ? 1 : 0);
              return (
                <Box key={p.month} sx={{ mb: 1.5 }}>
                  <Typography variant="body2">
                    <strong>{monthLabel(p.month)}:</strong> corte el {formatDayMonth(effective, p.month)},{" "}
                    {untilLabel(groupDialog.time || null)}, para {groupDialog.natures.length}{" "}
                    {groupDialog.natures.length === 1 ? "naturaleza" : "naturalezas"}.
                    {notes === 0 && " Sin conflictos."}
                  </Typography>
                  {p.clampedDay && (
                    <Alert severity="info" sx={{ mt: 0.5 }}>
                      {monthLabel(p.month)} no tiene día {p.day}: el corte será el {p.clampedDay}.
                    </Alert>
                  )}
                  {p.mergeWith && (
                    <Alert severity="info" sx={{ mt: 0.5 }}>
                      Ya hay un grupo con el mismo día y hora ({p.mergeWith.natures.join(", ")}): se unen en uno solo.
                    </Alert>
                  )}
                  {p.moves.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      Salen de otro grupo:{" "}
                      {p.moves.map((m) => `${m.nature} (estaba en el ${settingLabel(m.from.day, m.from.time)})`).join("; ")}.
                    </Alert>
                  )}
                  {p.ownOverrides.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 0.5 }}>
                      Tienen configuración propia, que seguirá mandando sobre el grupo. Marca las que quieras borrar:
                      <Box>
                        {p.ownOverrides.map((o) => (
                          <FormControlLabel
                            key={o.nature}
                            control={(
                              <Checkbox
                                size="small"
                                checked={(groupDialog.removeOwn[p.month] || []).includes(o.nature)}
                                onChange={() => toggleRemoveOwn(p.month, o.nature)}
                              />
                            )}
                            label={<Typography variant="body2">Borrar la de {o.nature} ({settingLabel(o.day, o.time)})</Typography>}
                          />
                        ))}
                      </Box>
                    </Alert>
                  )}
                </Box>
              );
            })}
            {dialogError && <Alert severity="warning" sx={{ mt: 2 }}>{dialogError}</Alert>}
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setGroupDialog(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !groupPlan.plan} onClick={handleGroupSave}>
            {groupMonths.length > 1 ? `Guardar en ${groupMonths.length} meses` : "Guardar grupo"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirmDialog?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancelar</Button>
          <Button variant="contained" color="error" disabled={saving} onClick={handleConfirm}>
            {confirmDialog?.confirm}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
